import { Link } from 'react-router-dom'
import { Button } from './Button'
import { ReactNode, useState } from 'react'

type EmptyStateVariant =
  | 'no-results'
  | 'no-listings'
  | 'empty-watchlist'
  | 'no-messages'
  | 'no-orders'
  | 'error'
  | 'custom'

interface EmptyStateProps {
  variant?: EmptyStateVariant
  title?: string
  description?: string
  icon?: string | ReactNode
  actionLabel?: string
  actionLink?: string
  onAction?: () => void
  className?: string
}

const VARIANTS: Record<EmptyStateVariant, { icon: string; image?: string; title: string; description: string; actionLabel?: string; actionLink?: string }> = {
  'no-results': {
    icon: '🔍',
    image: '/images/tui-no-results.png',
    title: 'No results found',
    description: 'Try adjusting your search or filters to find what you\'re looking for.',
    actionLabel: 'Clear Filters',
  },
  'no-listings': {
    icon: '📦',
    image: '/images/tui-no-results.png',
    title: 'No listings yet',
    description: 'Be the first to list something in this category!',
    actionLabel: 'Create Listing',
    actionLink: '/create',
  },
  'empty-watchlist': {
    icon: '❤️',
    image: '/images/tui-empty-watchlist.png',
    title: 'Your watchlist is empty',
    description: 'Save listings you\'re interested in by clicking the heart icon.',
    actionLabel: 'Browse Listings',
    actionLink: '/browse',
  },
  'no-messages': {
    icon: '💬',
    image: '/images/tui-no-messages.png',
    title: 'No messages yet',
    description: 'When you contact sellers or receive enquiries, they\'ll appear here.',
    actionLabel: 'Browse Listings',
    actionLink: '/browse',
  },
  'no-orders': {
    icon: '🛒',
    image: '/images/tui-empty-watchlist.png',
    title: 'No orders yet',
    description: 'When you make a purchase, your orders will appear here.',
    actionLabel: 'Browse Listings',
    actionLink: '/browse',
  },
  'error': {
    icon: '😕',
    image: '/images/tui-error.png',
    title: 'Something went wrong',
    description: 'We couldn\'t load this content. Please try again.',
    actionLabel: 'Try Again',
  },
  'custom': {
    icon: '📋',
    title: 'Nothing here',
    description: 'There\'s nothing to show at the moment.',
  },
}

export function EmptyState({
  variant = 'custom',
  title,
  description,
  icon,
  actionLabel,
  actionLink,
  onAction,
  className = '',
}: EmptyStateProps) {
  const defaults = VARIANTS[variant]

  const displayIcon = icon ?? defaults.icon
  const displayImage = defaults.image
  const displayTitle = title ?? defaults.title
  const displayDescription = description ?? defaults.description
  const displayActionLabel = actionLabel ?? defaults.actionLabel
  const displayActionLink = actionLink ?? defaults.actionLink

  const hasAction = displayActionLabel && (displayActionLink || onAction)
  const [imgError, setImgError] = useState(false)

  return (
    <div className={`text-center py-12 px-4 ${className}`}>
      {/* Icon / Mascot */}
      <div className="mb-6">
        {icon ? (
          typeof icon === 'string' ? (
            <div className="w-24 h-24 mx-auto bg-slate-100 rounded-full flex items-center justify-center">
              <span className="text-5xl">{icon}</span>
            </div>
          ) : (
            <div className="w-24 h-24 mx-auto bg-slate-100 rounded-full flex items-center justify-center text-slate-400">
              {icon}
            </div>
          )
        ) : displayImage && !imgError ? (
          <img
            src={displayImage}
            alt={displayTitle}
            className="w-32 h-32 mx-auto object-contain"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-24 h-24 mx-auto bg-slate-100 rounded-full flex items-center justify-center">
            <span className="text-5xl">{displayIcon}</span>
          </div>
        )}
      </div>

      {/* Title */}
      <h3 className="text-xl font-semibold text-slate-900 mb-2">
        {displayTitle}
      </h3>

      {/* Description */}
      <p className="text-slate-500 mb-6 max-w-sm mx-auto">
        {displayDescription}
      </p>

      {/* Action */}
      {hasAction && (
        displayActionLink ? (
          <Link to={displayActionLink}>
            <Button>{displayActionLabel}</Button>
          </Link>
        ) : (
          <Button onClick={onAction}>{displayActionLabel}</Button>
        )
      )}
    </div>
  )
}

// Specific empty state components for convenience
export function NoResultsState({ onClearFilters }: { onClearFilters?: () => void }) {
  return (
    <EmptyState
      variant="no-results"
      onAction={onClearFilters}
    />
  )
}

export function NoListingsState() {
  return <EmptyState variant="no-listings" />
}

export function EmptyWatchlistState() {
  return <EmptyState variant="empty-watchlist" />
}

export function NoMessagesState() {
  return <EmptyState variant="no-messages" />
}

export function ErrorState({ onRetry }: { onRetry?: () => void }) {
  return (
    <EmptyState
      variant="error"
      onAction={onRetry}
    />
  )
}
